import { useLocation, useNavigate } from 'react-router-dom';
import { Fragment, useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { nachAlphabet } from '../../utils/alphabetisch';
import { 
  LayoutDashboard, 
  Users, 
  FolderKanban, 
  CheckSquare, CalendarClock,
  Calendar, 
  FileText,
  Mail,
  Settings,
  ReceiptEuro,
  LineChart,
  Megaphone,
  X,
  Target,
  Car,
  UserCircle,
  MessageSquare,
  PartyPopper,
  UserSearch,
  Users2,
  Sparkles,  Briefcase,
  CalendarCheck,
  BookText,
  Zap,
  Plus,
  ChevronDown,
  ChevronRight,
  Brain,
  Wallet,
  CreditCard,
  Building2,
  TrendingUp,
  GraduationCap,
  CalendarDays,
  BookOpen,
  ShoppingBasket,
  UtensilsCrossed,
  Heart,
  Scale,
  Clock,
  ShieldAlert,
  LifeBuoy,
  Gavel,
  Inbox,
  ClipboardList,
  Loader2,
  PanelLeftClose
} from 'lucide-react';
import emailService from '../../services/emailService';
import teamsService from '../../services/teamsService';
import api from '../../services/api';
import useResizable from '../../hooks/useResizable';
import recentlyVisitedService from '../../services/recentlyVisitedService';
import { useTabs } from '../../contexts/TabContext';
import { useAufgabenFenster } from '../../contexts/AufgabenFensterContext';
import { useTerminFenster } from '../../contexts/TerminFensterContext';
import { useAuth } from '../../contexts/AuthContext';import { useChat } from '../../contexts/ChatContext';
import { useLegalWorkspace } from '../../hooks/useLegalWorkspace';
import {
  ContextMenu,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuSeparator,
  ContextMenuTrigger,
} from '../ui/context-menu';
import { useTenantIndustries } from '../../hooks/useTenantIndustries';
import useNotizbuch from '../../hooks/useNotizbuch';
import useEasyWorkChat from '../../hooks/useEasyWorkChat';
import { useMobileSidebar } from './Layout';

const ICON_MAP = {
  CalendarClock, LayoutDashboard, Users, FolderKanban, CheckSquare, Calendar, FileText,
  Mail, ReceiptEuro, LineChart, Megaphone, Target, Car, UserCircle,
  MessageSquare, PartyPopper, UserSearch, Users2, Sparkles, Briefcase,
  CalendarCheck, Zap, Brain, Settings, BookText, Wallet, CreditCard,
  Building2, TrendingUp, ShoppingBasket, GraduationCap, CalendarDays, BookOpen, UtensilsCrossed, Heart,
  Scale, Clock, ShieldAlert, Gavel, Inbox, ClipboardList, LifeBuoy,
  Calculator: CreditCard,  // Fallback für Calculator-Icon (Lucide-Import spart Bundle-Größe)
};

export function Sidebar({ onNavigate, fullWidth = false }) {
  const { t, i18n } = useTranslation();
  const sprache = i18n?.language || 'de';
  const location = useLocation();
  const navigate = useNavigate();
  const { oeffnen: aufgabenFensterOeffnen } = useAufgabenFenster();
  const { oeffnen: terminFensterOeffnen } = useTerminFenster();
  const openNotizbuch = useNotizbuch();
  const openChat = useEasyWorkChat();
  const { openTab, tabs } = useTabs();
  const layoutNav = useMobileSidebar();
  const { user } = useAuth();
  const { unreadTotal: chatUnreadTotal } = useChat();
  const isStudent = !!user?.is_student;
  const isFamily = (user?.tenant_type || 'business') === 'family';
  const isGastro = (user?.tenant_type || 'business') === 'gastro';
  const { isLegal } = useLegalWorkspace();

  // Firmenlogo aus TenantProfile für Sidebar
  const [companyLogo, setCompanyLogo] = useState({ url: '', enabled: false });
  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get('/tenant-profile');
        const prof = r.data?.profile || r.data || {};
        if (!cancelled) {
          setCompanyLogo({
            url: prof.logo_url || '',
            enabled: !!prof.show_logo_in_sidebar,
          });
        }
      } catch { /* ignore */ }
    })();
    // Reagiere auf Firmenprofil-Änderungen (custom event dispatched von CompanyProfilePage)
    const handler = (ev) => {
      const d = ev?.detail || {};
      setCompanyLogo({
        url: d.logo_url || '',
        enabled: !!d.show_logo_in_sidebar,
      });
    };
    window.addEventListener('tenant-profile-updated', handler);
    return () => { cancelled = true; window.removeEventListener('tenant-profile-updated', handler); };
  }, [user]);
  // Orderboard-Modul-Status: implizit aktiv bei Gastro-Tenants, sonst SuperAdmin-Toggle.
  const [orderboardActive, setOrderboardActive] = useState(isGastro);
  useEffect(() => {
    let cancelled = false;
    if (!user) return;
    if (isGastro) { setOrderboardActive(true); return; }
    if (isFamily || isStudent) { setOrderboardActive(false); return; }
    (async () => {
      try {
        const r = await api.get('/orderboard/status');
        if (!cancelled) setOrderboardActive(!!r.data?.active);
      } catch { if (!cancelled) setOrderboardActive(false); }
    })();
    return () => { cancelled = true; };
  }, [user, isGastro, isFamily, isStudent]);
  // Gastro ist ein Superset von Business: sieht/nutzt alle Business-Features (sofern freigeschaltet).
  const isBusinessLike = ((user?.tenant_type || 'business') === 'business') || isGastro || ((user?.tenant_type) === 'legal');

  // Dynamischer EasyWork-Variantenname für das Logo (Business Intelligence / Gastro / Mitglieder+ / Student / Family)
  const tenantVariantLabel =
    isStudent || (user?.tenant_type === 'student') ? 'Student'
      : isFamily ? 'Family'
      : isGastro ? 'Gastro'
      : (user?.tenant_type === 'mitglieder_plus') ? 'Mitglieder+'
      : 'Business Intelligence';

  // Resizable Breite (nur Desktop). ew_-Prefix + localStorage-Persistenz.
  const { width: sidebarWidth, startResize } = useResizable('ew_sidebar_width', 240, 160, 320);
  const resizable = !onNavigate && !fullWidth;

  // Erlaubte Hrefs für Studenten-Mandanten (Whitelist)
  const STUDENT_ALLOWED_HREFS = new Set([
    '/dashboard', '/addresses', '/projects', '/tasks', '/documents',
    '/calendar', '/emails', '/notes', '/ai-assistant', '/ai-workspace', '/posteingang',
    '/student/grades', '/student/schedule', '/student/studyplan', '/student/bewerbung',
    '/student/ki', '/student/post',
  ]);
  const filterItemsForStudent = (items = []) =>
    isStudent ? items.filter(it => STUDENT_ALLOWED_HREFS.has(it.href)) : items;
  // Branchen-Gate (z. B. Immobilien-spezifische FinanzManager-Einträge)
  const { hasIndustry } = useTenantIndustries();
  const filterItemsByIndustryGate = (items = []) =>
    items.filter(it => !it.industry_gate || hasIndustry(it.industry_gate));
  const filterItemsByAddonGate = (items = []) =>
    items.filter(it => !it.addon_gate || activeAddonKeys.has(it.addon_gate));
  const [unreadCount, setUnreadCount] = useState(0);
  const [teams, setTeams] = useState([]);
  const [teamsLoading, setTeamsLoading] = useState(true);
  const [expandedTeams, setExpandedTeams] = useState({});
  const [recentProjects, setRecentProjects] = useState([]);
  const [recentAddresses, setRecentAddresses] = useState([]);
  const [sidebarData, setSidebarData] = useState({ teams: [] });
  const [aiConnected, setAiConnected] = useState(null);
  const [hasMarketingProject, setHasMarketingProject] = useState(false);
  // Projekttyp „MarketingManager“: id des Workspace-Projekts (für Aktiv-Markierung in der Seitenleiste)
  const [marketingProjektId, setMarketingProjektId] = useState(null);
  const [marketingAddonActive, setMarketingAddonActive] = useState(false);
  const [vertragsmanagerActive, setVertragsmanagerActive] = useState(true);  // default: aktiv für Bestandstenants
  const [lohnabrechnungActive, setLohnabrechnungActive] = useState(true);    // default: aktiv für Bestandstenants
  // Aktive Add-on-Keys (für generisches `addon_gate` in Sidebar-Items).
  const [activeAddonKeys, setActiveAddonKeys] = useState(() => new Set());

  // Pflichtenheft A1: Marketing-Eintrag nur wenn ein "marketing_manager"-Projekt existiert
  // oder das Marketing-Profile bereits onboardet ist.
  // Add-on-Pflichtenheft E1: Zusätzlich muss `addons.marketing_manager.status === 'active'` sein.
  // Fix 09/2026: MarketingManager ist auch für nicht-business-Tenants (z.B. mitglieder_plus,
  // sportverein) buchbar. Nur Studenten und Familien werden ausgeschlossen; das Add-on/Modul
  // entscheidet, ob der Eintrag sichtbar ist.
  useEffect(() => {
    if (isStudent || isFamily) {
      setHasMarketingProject(false);
      setMarketingAddonActive(false);
      setVertragsmanagerActive(false);
      setLohnabrechnungActive(false);
      return;
    }
    let cancelled = false;
    import('../../services/api').then(({ default: api }) => {
      Promise.all([
        // Legt das MarketingManager-Projekt an, falls freigeschaltet und noch nicht vorhanden
        api.get('/marketing/projekt').catch(() => ({ data: { projekt: null } })),
        api.get('/marketing/profile').catch(() => ({ data: { onboarding_completed: false } })),
        api.get('/addons/my').catch(() => ({ data: { items: [] } })),
        api.get('/tenant/me').catch(() => ({ data: { modules: [] } })),
      ]).then(([projRes, profRes, addonRes, meRes]) => {
        if (cancelled) return;
        const hasProj = !!projRes.data?.projekt?.id;
        setMarketingProjektId(projRes.data?.projekt?.id || null);
        const onboarded = !!profRes.data?.onboarding_completed;
        const items = addonRes.data?.items || [];
        const mm = items.find(i => i.key === 'marketing_manager');
        const vm = items.find(i => i.key === 'vertragsmanager');
        const lo = items.find(i => i.key === 'lohnabrechnung');
        // Single Source of Truth: tenant.modules (key -> active). Leere Liste = Fallback "alles an".
        const mods = meRes.data?.modules || [];
        const modMap = {};
        mods.forEach(m => { modMap[m.key] = m.active; });
        const isActive = (k) => (mods.length === 0 ? true : !!modMap[k]);
        // Workspace-Sichtbarkeit: Add-on-Status ODER Modul aktiv – unabhängig vom Tarif (auch Free).
        setMarketingAddonActive(mm?.status === 'active' || isActive('marketing_manager'));
        // Alle aktiven Add-ons sammeln (für generischen `addon_gate`-Filter der Sidebar-Items)
        const activeKeys = new Set(items.filter((it) => it?.status === 'active').map((it) => it.key));
        setActiveAddonKeys(activeKeys);
        // Vertragsmanager & Lohnabrechnung sind Business-only.
        if (isBusinessLike) {
          const vmAddonOk = !vm || vm.status === 'active';
          const vmModuleOn = isActive('vertragsmanager');
          const vmModuleBlocked = mods.length > 0 && modMap['vertragsmanager'] === false;
          setVertragsmanagerActive(vmModuleOn || (vmAddonOk && !vmModuleBlocked));
          const loAddonOk = !lo || lo.status === 'active';
          setLohnabrechnungActive(loAddonOk || isActive('hr'));
        } else {
          setVertragsmanagerActive(false);
          setLohnabrechnungActive(false);
        }
        setHasMarketingProject(hasProj || onboarded);
      });
    });
    return () => { cancelled = true; };
  }, [user?.tenant_type, isStudent, isFamily, isBusinessLike]);

  // Teams + Sidebar-Struktur laden
  const loadTeams = useCallback(async () => {
    try {
      try { await teamsService.initializeBaseTeams(); } catch { /* already initialized */ }
      const [teamsRes, sidebarRes] = await Promise.all([
        teamsService.getTeams(),
        teamsService.getSidebarStructure()
      ]);
      setTeams(teamsRes.teams || []);
      setSidebarData(sidebarRes || { teams: [] });
      setExpandedTeams(prev => {
        const merged = { ...prev };
        (teamsRes.teams || []).forEach(t => {
          if (merged[t.team_type] === undefined) merged[t.team_type] = false;
        });
        return merged;
      });
    } catch (error) {
      console.error('Teams laden fehlgeschlagen:', error);
    } finally {
      setTeamsLoading(false);
    }
  }, []);

  // Letzte Projekte und Adressen laden
  const loadRecentItems = useCallback(() => {
    try {
      const all = recentlyVisitedService.getAll();
      setRecentProjects(all.filter(i => i.type === 'project').slice(0, 5));
      setRecentAddresses(all.filter(i => i.type === 'address').slice(0, 5));
    } catch { /* ignore */ }
    try {
      const stored = localStorage.getItem('recentProjects');
      if (stored) {
        const parsed = JSON.parse(stored);
        setRecentProjects(prev => prev.length > 0 ? prev : parsed.map(p => ({ ...p, type: 'project', link: `/projects/${p.id}` })));
      }
    } catch { /* ignore */ }
  }, []);

  useEffect(() => {
    loadTeams();
    loadRecentItems();
    // KI-Verbindungsstatus pruefen
    import('../../services/api').then(({ default: api }) => {
      api.get('/ai-assistant/status').then(r => setAiConnected(r.data?.connected)).catch(() => setAiConnected(false));
    });

    const refreshAiStatus = () => {
      import('../../services/api').then(({ default: api }) => {
        api.get('/ai-assistant/status').then(r => setAiConnected(r.data?.connected)).catch(() => setAiConnected(false));
      });
    };

    const handleTeamsUpdate = () => loadTeams();
    window.addEventListener('teamsUpdated', handleTeamsUpdate);
    window.addEventListener('storage', loadRecentItems);
    window.addEventListener('recentProjectsUpdated', loadRecentItems);
    window.addEventListener('recentItemsUpdated', loadRecentItems);
    window.addEventListener('ai-status-changed', refreshAiStatus);
    return () => {
      window.removeEventListener('teamsUpdated', handleTeamsUpdate);
      window.removeEventListener('storage', loadRecentItems);
      window.removeEventListener('recentProjectsUpdated', loadRecentItems);
      window.removeEventListener('recentItemsUpdated', loadRecentItems);
      window.removeEventListener('ai-status-changed', refreshAiStatus);
    };
  }, [loadTeams, loadRecentItems]);

  // Aktives Team aufklappen basierend auf aktueller Route
  useEffect(() => {
    teams.forEach(team => {
      const items = team.sidebar_items || [];
      if (items.some(item => location.pathname.startsWith(item.href))) {
        setExpandedTeams(prev => ({ ...prev, [team.team_type]: true }));
      }
    });
  }, [location.pathname, teams]);

  // Unread E-Mail Count
  useEffect(() => {
    const fetchUnread = async () => {
      try {
        const result = await emailService.getUnreadCount();
        setUnreadCount(result?.count || 0);
      } catch { /* ignore */ }
    };
    fetchUnread();
    const interval = setInterval(fetchUnread, 120000);
    return () => clearInterval(interval);
  }, []);

  // Rechtsklick-Aktionen
  const projectContextActions = [
    { label: 'Neues Projekt', action: () => navigate('/projects', { state: { openNew: true } }) },
    ...(recentProjects.length > 0 ? [{ label: '---separator---' }] : []),
    ...recentProjects.slice(0, 5).map(p => ({
      label: p.name,
      action: () => {
        const projPath = p.link || `/projects/${p.id}`;
        const tabId = `projects-${p.id}`;
        openTab(projPath, p.name, 'FolderKanban', tabId);
        navigate(projPath);
        if (onNavigate) onNavigate();
      }
    }))
  ];

  const addressContextActions = [
    { label: 'Neue Adresse', action: () => navigate('/addresses', { state: { openNew: true } }) },
    { label: 'Adressen importieren', action: () => navigate('/settings?tab=data&autoImport=addresses') },
    ...(recentAddresses.length > 0 ? [{ label: '---separator---' }] : []),
    ...recentAddresses.slice(0, 5).map(a => ({
      label: a.name,
      action: () => { navigate(a.link || `/addresses/${a.id}`); if (onNavigate) onNavigate(); }
    }))
  ];

  const rightClickActions = {
    '/dashboard': { label: 'Dashboard anpassen', action: () => navigate('/dashboard?edit=true') },
    '/addresses': addressContextActions,
    '/leads': { label: 'Neuer Lead', action: () => navigate('/leads', { state: { openNew: true } }) },
    '/projects': projectContextActions,
    '/tasks': { label: 'Neue Aufgabe', action: () => aufgabenFensterOeffnen() },
    '/calendar': { label: 'Neuer Termin', action: () => terminFensterOeffnen() },
    '/emails': { label: 'Neue E-Mail', action: () => navigate('/emails', { state: { openNew: true } }) },
    '/orders': { label: 'Neuer Auftrag anlegen', action: () => navigate('/orders', { state: { openNew: true } }) },
    '/campaigns': { label: 'Neue Kampagne', action: () => navigate('/campaigns', { state: { openNew: true } }) },
    '/ai-assistant': { label: 'Neue Konversation', action: () => navigate('/ai-assistant', { state: { newChat: true } }) },
    '/documents': { label: 'Dokument hochladen', action: () => navigate('/documents', { state: { openUpload: true } }) },
    '/notes': { label: 'Neue Notiz', action: () => openNotizbuch({ openNew: true }) },
  };

  const isActive = (path) => {
    if (path === '/dashboard') return location.pathname === '/dashboard';
    // Pfade mit Query-Params exakt vergleichen
    if (path.includes('?')) {
      return (location.pathname + location.search) === path;
    }
    return location.pathname.startsWith(path);
  };

  const navigateToBuchhaltung = async () => {
    const year = new Date().getFullYear();
    try {
      const res = await api.get('/projects?project_type=Buchhaltung&limit=100');
      const list = Array.isArray(res.data) ? res.data : (res.data?.projects || []);
      const proj =
        list.find(p => (p.name || '').includes(String(year))) || list[0];
      if (proj?.id) {
        const path = `/projects/${proj.id}?tab=accounting`;
        openTab(path, proj.name || `Buchhaltung ${year}`, 'ReceiptEuro', `projects-${proj.id}`);
        navigate(path);
        if (onNavigate) onNavigate();
        return;
      }
    } catch (e) { /* Fallback unten */ }
    // Fallback: gefilterte Projektliste
    openTab('/projects?project_type=Buchhaltung', 'Buchhaltung', 'ReceiptEuro', 'projectsbuchhaltung');
    navigate('/projects?project_type=Buchhaltung');
    if (onNavigate) onNavigate();
  };

  // MarketingManager ist ein Projekttyp: wie „Buchhaltung“ direkt ins Projekt springen,
  // bei mehreren Projekten die nach Projekttyp gefilterte Projektliste öffnen.
  const navigateToMarketing = async () => {
    try {
      const res = await api.get('/projects?project_type=MarketingManager&limit=100');
      const list = Array.isArray(res.data) ? res.data : (res.data?.projects || []);
      if (list.length === 1 && list[0]?.id) {
        const path = `/projects/${list[0].id}`;
        openTab(path, list[0].name || 'MarketingManager', 'Megaphone', `projects-${list[0].id}`);
        navigate(path);
        if (onNavigate) onNavigate();
        return;
      }
      if (list.length > 1) {
        openTab('/projects?project_type=MarketingManager', 'MarketingManager', 'Megaphone', 'projectsmarketingmanager');
        navigate('/projects?project_type=MarketingManager');
        if (onNavigate) onNavigate();
        return;
      }
    } catch (e) { /* Fallback unten */ }
    // Noch kein Projekt (z. B. nicht freigeschaltet): /marketing legt es an bzw. zeigt die Freischaltung
    handleNavClick('/marketing', 'MarketingManager', 'Megaphone');
  };

  const handleNavClick = (href, name, icon) => {
    // Notizbuch und EasyWorkChat laufen als Seite im Aufgaben-Stil (Hooks öffnen Tab + Route)
    if (href === '/notes') {
      openNotizbuch();
      if (onNavigate) onNavigate();
      return;
    }
    if (href === '/chat') {
      openChat();
      if (onNavigate) onNavigate();
      return;
    }
    const tabId = href === '/dashboard' ? 'dashboard' : href.replace(/\//g, '');
    // Ist der Tab schon offen, dorthin zurück, wo er zuletzt stand (inkl. Filter/Ansicht in der URL)
    const offen = tabs.find((t) => t.id === tabId && !t.virtual && t.path && t.path.split('?')[0] === href);
    openTab(href, name, icon, tabId);
    navigate(offen ? offen.path : href);
    if (onNavigate) onNavigate();
  };

  const toggleTeam = (teamType) => {
    setExpandedTeams(prev => ({ ...prev, [teamType]: !prev[teamType] }));
  };

  // Rechtsklick auf die Navigationsleiste öffnet die Einstellungen (nur Admin, wie der
  // Einstellungen-Button im Footer). e.defaultPrevented ist bereits true, wenn ein
  // Nav-Item mit eigenem Kontextmenü (z. B. Projekte/Adressen) den Rechtsklick schon
  // behandelt hat — dann greift dieser Handler nicht.
  const handleSidebarContextMenu = (e) => {
    if (e.defaultPrevented || user?.role !== 'admin') return;
    e.preventDefault();
    openTab('/settings', 'Einstellungen', 'Settings', 'settings');
    navigate('/settings');
    if (onNavigate) onNavigate();
  };

  // Backend liefert Team- und Sub-Item-Namen hartcodiert auf Deutsch.
  // Auf Frontend-Seite übersetzen wir sie via i18n mit Fallback aufs Backend.
  const TEAM_TYPE_LABEL = {
    team_crm: 'nav.teamCrm',
    team_finance: 'nav.teamFinance',
    team_members: 'nav.teamMembers',
    team_marketing: 'nav.teamMarketing',
    team_operations: 'nav.teamOperations',
    team_hr: 'nav.teamHr',
    team_recruiting: 'nav.teamRecruiting',
  };
  const HREF_TO_NAV_KEY = {
    '/addresses': 'nav.addresses',
    '/leads': 'nav.leads',
    '/projects': 'nav.projects',
    '/projects?project_type=Buchhaltung': 'nav.accounting',
    '/controlling': 'nav.controlling',
    '/orders': 'nav.orders',
    '/members': 'nav.members',
    '/tasks': 'nav.tasks',
    '/calendar': 'nav.calendar',
    '/emails': 'nav.emails',
    '/posteingang': 'nav.posteingang',
    '/chat': 'nav.easyworkchat',
    '/notes': 'nav.notes',
    '/contracts': 'nav.contracts',
    '/payroll': 'nav.payroll',
    '/hr': 'nav.hr',
    '/campaigns': 'nav.campaigns',
    '/documents': 'nav.documents',
    '/invoices': 'nav.invoices',
    '/workflows': 'nav.workflows',
  };
  const localizeItemName = (item) => {
    const key = HREF_TO_NAV_KEY[item.href];
    return key ? t(key, item.name) : item.name;
  };
  const localizeTeamName = (team) => {
    const key = TEAM_TYPE_LABEL[team.team_type];
    // Beim Fehlen eines Mappings Original aus DB nutzen
    return key ? t(key, team.display_name) : team.display_name;
  };

  // Lohnabrechnung: im Personal-Team (lila) statt unter Werkzeuge, sofern das Team aktiv ist
  const lohnabrechnungSichtbar = isBusinessLike && !isStudent && lohnabrechnungActive;
  const hatPersonalTeam = teams.some((tm) => tm.team_type === 'team_hr' && tm.status === 'active');
  const lohnabrechnungImWerkzeug = lohnabrechnungSichtbar && !hatPersonalTeam;
  const LOHN_ITEM = { name: t('nav.payroll'), href: '/payroll', icon: 'Wallet' };

  // Gemeinsame Items die immer sichtbar sind (unabhaengig von Teams)
  const baseGlobalItems = [
    { name: t('nav.calendar'), href: '/calendar', icon: 'Calendar' },
    { name: t('nav.tasks'), href: '/tasks', icon: 'CheckSquare' },
    { name: t('nav.wiedervorlagen'), href: '/wiedervorlagen', icon: 'CalendarClock' },
    { name: t('nav.posteingang'), href: '/posteingang', icon: 'Inbox' },
    { name: t('nav.emails'), href: '/emails', icon: 'Mail' },
    { name: t('nav.easyworkchat'), href: '/chat', icon: 'MessageSquare', badgeKey: 'chat' },
    { name: t('nav.notes'), href: '/notes', icon: 'BookText' },
    ...(vertragsmanagerActive
      ? [{ name: t('nav.contracts'), href: '/contracts', icon: 'FileText' }]
      : []),
    // Lohnabrechnung gehört zum Personal-Team; nur ohne aktives Personal-Team hier unter Werkzeuge
    ...(lohnabrechnungImWerkzeug
      ? [{ name: t('nav.payroll'), href: '/payroll', icon: 'Wallet' }]
      : []),
  ];
  // Family-Mode: TeamComm → "Familien-Chat", Kalender → "Familienkalender"
  const familyGlobalItems = [
    { name: t('nav.familyCalendar'), href: '/calendar', icon: 'Calendar' },
    { name: t('nav.posteingang'), href: '/posteingang', icon: 'Inbox' },
    { name: t('nav.emails'), href: '/emails', icon: 'Mail' },
    { name: t('nav.easyworkchat'), href: '/chat', icon: 'MessageSquare', badgeKey: 'chat' },
    { name: t('nav.notes'), href: '/notes', icon: 'BookText' },
    { name: t('nav.contracts'), href: '/contracts', icon: 'FileText' },
  ];
  // Gastro-Mode: Gastronomie-Shortcut + alle Business-Items.
  // Der Orderboard-Zugang ist im Gastronomie-Untermenü (Tab „Orderboard") integriert,
  // damit er nicht doppelt in der Sidebar erscheint.
  const gastroGlobalItems = [
    { name: t('nav.gastro'), href: '/gastro', icon: 'UtensilsCrossed' },
    ...baseGlobalItems,
  ];
  const globalItems = isFamily
    ? familyGlobalItems
    : isGastro
      ? gastroGlobalItems
      : filterItemsForStudent(baseGlobalItems);

  // Items, die direkt unter "Meine Teams" erscheinen (Sektion abschliessend)
  // "Mein Erfolgsteam" – nur für business/gastro/mitglieder_plus (nicht family/student)
  const tType = (user?.tenant_type || 'business');
  const showErfolgsteam = (tType === 'business' || tType === 'gastro' || tType === 'legal' || tType === 'mitglieder_plus') && !isStudent;
  const baseMyTeamItems = [
    ...(showErfolgsteam ? [{ name: t('nav.erfolgsteam'), href: '/erfolgsteam', icon: 'Users' }] : []),
    { name: t('nav.consulting'), href: '/consulting', icon: 'Brain' },
    // KrisenManager: im Tarif enthalten (kein Add-on), für Business-Mandanten wie „Mein Erfolgsteam"
    ...(showErfolgsteam ? [{ name: t('nav.krisenManager'), href: '/krisenmanager', icon: 'LifeBuoy' }] : []),
  ];
  const myTeamItems = filterItemsForStudent(baseMyTeamItems);

  // Nav-Item rendern
  const renderNavItem = (name, href, iconName, badge = 0, isSubItem = false) => {
    const Icon = ICON_MAP[iconName] || FileText;
    const active = isActive(href);
    const rightClickAction = rightClickActions[href];

    const buttonContent = (
      <button
        onClick={() => handleNavClick(href, name, iconName)}
        data-testid={`nav-${(href || name).toString().toLowerCase().replace(/^\/+/, '').replace(/\//g, '-').replace(/\s+/g, '-').replace(/[äöü]/g, c => ({ä:'ae',ö:'oe',ü:'ue'}[c])) || 'root'}`}
        className={`
          w-full flex items-center px-3 ${isSubItem ? 'py-1.5 pl-9' : 'py-2'} text-[12px] rounded-lg
          transition-colors duration-150 text-left border-l-2
          ${active
            ? 'bg-white/10 text-white font-semibold border-l-[#E67E22]'
            : isSubItem
              ? 'text-white/55 hover:bg-white/5 hover:text-white/90 font-medium border-l-transparent'
              : 'text-white/60 hover:bg-white/5 hover:text-white/90 font-medium border-l-transparent'
          }
        `}
      >
        <Icon className={`${isSubItem ? 'w-4 h-4' : 'w-5 h-5'} mr-3`} />
        {name}
        {badge > 0 && (
          <span className="ml-auto bg-red-500 text-white text-xs font-bold px-2 py-0.5 rounded-full">
            {badge > 99 ? '99+' : badge}
          </span>
        )}
      </button>
    );

    // Aufgaben/Kalender: „+“ öffnet direkt das Aufgaben- bzw. Terminfenster (dasselbe wie in Adressen und Projekten)
    const schnellanlage = { '/tasks': ['Neue Aufgabe', aufgabenFensterOeffnen], '/calendar': ['Neuer Termin', terminFensterOeffnen] }[href];
    const mitSchnellanlage = schnellanlage ? (
      <div className="relative group/neu">
        {buttonContent}
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); schnellanlage[1](); }}
          title={schnellanlage[0]}
          aria-label={schnellanlage[0]}
          className="absolute right-1.5 top-1/2 -translate-y-1/2 w-6 h-6 rounded-md grid place-items-center text-white/50 hover:text-white hover:bg-white/10 opacity-0 group-hover/neu:opacity-100 focus:opacity-100"
          data-testid={href === '/tasks' ? 'nav-tasks-neu' : 'nav-calendar-neu'}
        >
          <Plus className="w-4 h-4" />
        </button>
      </div>
    ) : buttonContent;

    if (!rightClickAction) return mitSchnellanlage;

    const actions = Array.isArray(rightClickAction) ? rightClickAction : [rightClickAction];
    return (
      <ContextMenu>
        <ContextMenuTrigger asChild>{mitSchnellanlage}</ContextMenuTrigger>
        <ContextMenuContent className="w-56">
          {actions.map((action, index) => (
            action.label === '---separator---' ? (
              <ContextMenuSeparator key={index} />
            ) : (
              <ContextMenuItem key={index} onClick={action.action} className="truncate">
                {action.label}
              </ContextMenuItem>
            )
          ))}
        </ContextMenuContent>
      </ContextMenu>
    );
  };

  // Teams nach Kategorie gruppieren
  const teamsByCategory = {};
  teams.forEach(team => {
    const cat = team.category || 'Sonstige';
    if (!teamsByCategory[cat]) teamsByCategory[cat] = [];
    teamsByCategory[cat].push(team);
  });

  return (
    <div
      className={`relative flex flex-col text-white ${fullWidth ? 'w-full h-full' : 'h-screen'}`}
      style={{ backgroundColor: 'var(--ew-nav-bg)', width: fullWidth ? '100%' : (resizable ? sidebarWidth : 256) }}
      data-testid="app-sidebar"
      onContextMenu={handleSidebarContextMenu}
    >
      {/* Logo — oranges Icon + EasyWork + BUSINESS INTELLIGENCE */}
      <div className="px-3 py-3 border-b border-white/10">
        <div className="flex items-center justify-between">
          <button
            className="flex items-center gap-2.5 min-w-0 flex-1 text-left cursor-pointer group"
            onClick={() => navigate('/')}
            data-testid="logo-link"
          >
            <span className="w-8 h-8 flex items-center justify-center shrink-0">
              <img src="/easywork-icon-white.png" alt="" className="w-7 h-7 object-contain" />
            </span>
            <span className="min-w-0 leading-tight">
              <span className="block text-[13px] font-bold text-white truncate group-hover:text-white">EasyWork</span>
              <span className="block text-[8px] font-semibold uppercase text-white/30" style={{ letterSpacing: '1.2px' }}>{tenantVariantLabel}</span>
            </span>
          </button>
          {/* Hauptnavigation einklappen (Desktop) – Gegenstück im Header blendet sie wieder ein */}
          {!onNavigate && !fullWidth && layoutNav?.toggleNav && (
            <button
              onClick={layoutNav.toggleNav}
              className="ml-2 p-1.5 hidden lg:inline-flex text-white/60 hover:text-white hover:bg-white/10 rounded-lg"
              title="Navigation einklappen"
              aria-label="Navigation einklappen"
              data-testid="hauptnav-einklappen"
            >
              <PanelLeftClose className="w-4 h-4" />
            </button>
          )}
          {onNavigate && (
            <button
              onClick={onNavigate}
              className="ml-2 p-2 lg:hidden text-white/60 hover:text-white hover:bg-white/10 rounded-lg"
              aria-label={t('nav.closeMenu')}
            >
              <X className="w-5 h-5" />
            </button>
          )}
        </div>
        {/* Firmenlogo (optional aus Firmenprofil) */}
        {companyLogo.enabled && companyLogo.url && (
          <div className="mt-3 pt-3 border-t border-white/10 flex items-center justify-center" data-testid="sidebar-company-logo">
            <img
              src={companyLogo.url}
              alt="Firmenlogo"
              className="max-h-12 max-w-full object-contain rounded bg-white/95 p-1.5"
              style={{ maxWidth: '85%' }}
            />
          </div>
        )}
      </div>

      {/* Navigation */}
      <nav className="flex-1 px-3 py-3 overflow-y-auto" style={{ scrollbarWidth: 'thin' }}>
        <div className="space-y-0.5">
          {/* Dashboard - immer oben */}
          {renderNavItem(t('nav.dashboard'), '/dashboard', 'LayoutDashboard')}

          {/* === STUDENTEN-MODUS: Flache Liste, kein Team-Header === */}
          {isStudent ? (
            <>
              <div className="pt-3 pb-1 px-3">
                <span className="text-[9px] font-bold uppercase tracking-[0.12em] text-white/30">{t('nav.sectionStudy')}</span>
              </div>
              {/* Alphabetisch nach übersetzter Beschriftung (Projektregel) */}
              {nachAlphabet([
                { name: t('nav.bewerbungsKompass'), href: '/student/bewerbung', icon: 'Briefcase' },
                { name: t('nav.contacts'),  href: '/addresses',         icon: 'Users' },
                { name: t('nav.fragDieKi'), href: '/student/ki',        icon: 'Sparkles' },
                { name: t('nav.uniPost'),   href: '/student/post',      icon: 'Inbox' },
                { name: t('nav.schedule'),  href: '/student/schedule',  icon: 'CalendarDays' },
                { name: t('nav.studyPlan'), href: '/student/studyplan', icon: 'BookOpen' },
                { name: t('nav.grades'),    href: '/student/grades',    icon: 'GraduationCap' },
                { name: t('nav.projects'),  href: '/projects',          icon: 'FolderKanban' },
                { name: "ToDo's",           href: '/tasks',             icon: 'CheckSquare' }, // EasyWork Student: „ToDo's“ statt „Aufgaben“ (Wunsch Ralph, 25.10.2026)
                { name: t('nav.documents'), href: '/documents',         icon: 'FileText' },
              ], (e) => e.name, sprache).map((e) => <div key={e.href}>{renderNavItem(e.name, e.href, e.icon)}</div>)}
            </>
          ) : isFamily ? (
            <>
              <div className="pt-3 pb-1 px-3">
                <span className="text-[9px] font-bold uppercase tracking-[0.12em] text-white/30">{t('nav.sectionFamily')}</span>
              </div>
              {renderNavItem(t('nav.familyContacts'), '/addresses', 'Users')}
              {renderNavItem(t('nav.familyProjects'),   '/projects',  'FolderKanban')}
              {renderNavItem(t('nav.tasks'),           '/tasks',     'CheckSquare')}
              {renderNavItem(t('nav.familyDocuments'),     '/documents', 'FileText')}
              {renderNavItem(t('nav.shopping'),      '/family/shopping', 'ShoppingBasket')}
              {renderNavItem(t('nav.recipes'),          '/family/recipes', 'UtensilsCrossed')}
              {renderNavItem(t('nav.beauty'),       '/family/beauty', 'Heart')}
              {renderNavItem(t('nav.budget'),      '/family/budget', 'Wallet')}
              {renderNavItem(t('nav.savings'),          '/family/savings-rate', 'TrendingUp')}
              {renderNavItem(t('nav.loans'),            '/family/loans',  'CreditCard')}
              {renderNavItem(t('nav.properties'),         '/family/properties', 'Building2')}
            </>
          ) : (
          <>
          {/* MEINE TEAMS */}
          {teamsLoading ? (
            <div className="flex items-center gap-2 px-3 py-3 text-white/50 text-xs">
              <Loader2 className="w-3 h-3 animate-spin" /> {t('nav.teamsLoading')}
            </div>
          ) : (
            <>
              <div className="pt-3 pb-1 px-3">
                <span className="text-[9px] font-bold uppercase tracking-[0.12em] text-white/30">{t('nav.sectionMyTeams')}</span>
              </div>

              {teams.filter(t2 => t2.status === 'active').length === 0 && (
                <button
                  onClick={() => { setTeamsLoading(true); loadTeams(); }}
                  className="w-full flex items-center gap-2 px-3 py-2 text-xs text-white/60 hover:bg-white/5 rounded"
                  data-testid="sidebar-teams-retry"
                >
                  <Loader2 className="w-3 h-3" />
                  {t('nav.teamsRetry')}
                </button>
              )}

              {/* Teams, team-übergreifende Punkte und MarketingManager gemeinsam alphabetisch (Projektregel) */}
              {nachAlphabet([
                ...teams.filter(t => t.status === 'active').filter((t, idx, arr) => arr.findIndex(x => x.team_type === t.team_type) === idx).map(team => ({ key: team.team_type, name: localizeTeamName(team), render: () => {
                const TeamIcon = ICON_MAP[team.icon] || Users;
                const isExpanded = expandedTeams[team.team_type] !== false;
                let sidebarItems = filterItemsByAddonGate(filterItemsByIndustryGate(filterItemsForStudent(team.sidebar_items || [])));
                if (team.team_type === 'team_hr' && lohnabrechnungSichtbar && !sidebarItems.some((i) => i.href === '/payroll')) {
                  sidebarItems = [...sidebarItems, LOHN_ITEM];
                }
                sidebarItems = nachAlphabet(sidebarItems, localizeItemName, sprache);
                // Projekte aus der Sidebar-Struktur
                const teamSidebar = sidebarData.teams?.find(s => s.team_type === team.team_type);
                const teamProjects = teamSidebar?.projects || [];

                // Bei Studenten: Team komplett ausblenden, wenn keine erlaubten Items darin sind
                // (Projekte werden in der Sidebar nicht mehr gelistet, zählen hier daher nicht mehr)
                if (isStudent && sidebarItems.length === 0) {
                  return null;
                }

                // Marketing-Team: Direkt-Navigation zum MarketingManager statt Toggle (Pflichtenheft 2026-05-20)
                if (team.team_type === 'team_marketing' && marketingAddonActive) {
                  const isMarketingActive = location.pathname.startsWith('/marketing') || (location.pathname === '/projects' && location.search.includes('project_type=MarketingManager')) || (!!marketingProjektId && location.pathname === `/projects/${marketingProjektId}`);
                  return (
                    <ContextMenu key={team.team_type}>
                      <ContextMenuTrigger asChild>
                        <button
                          onClick={() => { navigateToMarketing(); }}
                          className={`w-full flex items-center px-3 py-2 text-[12px] font-semibold rounded-lg transition-colors border-l-2 ${
                            isMarketingActive
                              ? 'bg-white/10 text-white border-l-[#E67E22]'
                              : 'text-white/60 hover:bg-white/5 hover:text-white/90 border-l-transparent'
                          }`}
                          data-testid="team-group-marketing-manager"
                        >
                          <div className="w-5 h-5 rounded flex items-center justify-center mr-3 shrink-0" style={{ backgroundColor: team.color }}>
                            <TeamIcon className="w-3 h-3 text-white" />
                          </div>
                          <span className="flex-1 text-left truncate">{t('nav.marketingManager')}</span>
                          <ChevronRight className="w-3.5 h-3.5 text-white/40 shrink-0" />
                        </button>
                      </ContextMenuTrigger>
                      <ContextMenuContent className="w-56">
                        <ContextMenuItem
                          onClick={() => navigate('/marketing?tab=kampagnen&ansicht=kampagnen&kampagne=neu&phase=planung')}
                          data-testid="ctx-marketing-new-campaign"
                        >
                          Neue Kampagne erstellen
                        </ContextMenuItem>
                      </ContextMenuContent>
                    </ContextMenu>
                  );
                }

                // Mitglieder-Team: Direkt-Navigation zu /members, damit der Team-Header
                // NICHT ein zweites redundantes „Mitglieder"-Sub-Item ausklappt. Untermenü-
                // Navigation (Belegungsplan, Anwesenheit, …) läuft über die Tabs der
                // MembersPage selbst.
                if (team.team_type === 'team_members' && teamProjects.length === 0) {
                  const isMembersActive = location.pathname.startsWith('/members');
                  const teamLabel = localizeTeamName(team);
                  return (
                    <button
                      key={team.team_type}
                      onClick={() => { handleNavClick('/members', teamLabel, team.icon); }}
                      className={`w-full flex items-center px-3 py-2 text-[12px] font-semibold rounded-lg transition-colors border-l-2 ${
                        isMembersActive
                          ? 'bg-white/10 text-white border-l-[#E67E22]'
                          : 'text-white/60 hover:bg-white/5 hover:text-white/90 border-l-transparent'
                      }`}
                      data-testid="team-group-members-direct"
                    >
                      <div className="w-5 h-5 rounded flex items-center justify-center mr-3 shrink-0" style={{ backgroundColor: team.color }}>
                        <TeamIcon className="w-3 h-3 text-white" />
                      </div>
                      <span className="flex-1 text-left truncate">{teamLabel}</span>
                      <ChevronRight className="w-3.5 h-3.5 text-white/40 shrink-0" />
                    </button>
                  );
                }

                return (
                  <div key={team.team_type}>
                    {/* Team-Header */}
                    <button
                      onClick={() => toggleTeam(team.team_type)}
                      className="w-full flex items-center px-3 py-2 text-[12px] font-semibold text-white/70 hover:bg-white/5 hover:text-white/90 rounded-lg transition-colors"
                      data-testid={`team-group-${team.team_type}`}
                    >
                      <div className="w-5 h-5 rounded flex items-center justify-center mr-3 shrink-0" style={{ backgroundColor: team.color }}>
                        <TeamIcon className="w-3 h-3 text-white" />
                      </div>
                      <span className="flex-1 text-left truncate">{localizeTeamName(team)}</span>
                      {sidebarItems.length > 0 && (
                        <ChevronRight
                          className="w-3.5 h-3.5 text-white/40 transition-transform duration-200"
                          style={{ transform: isExpanded ? 'rotate(90deg)' : 'rotate(0deg)' }}
                        />
                      )}
                    </button>

                    {/* Team-Sub-Items + Projekte */}
                    {isExpanded && (
                      <>
                        {sidebarItems.map(item => (
                          <div key={item.href}>
                            {renderNavItem(
                              localizeItemName(item),
                              item.href,
                              item.icon,
                              item.href === '/emails' ? unreadCount : 0,
                              true
                            )}
                          </div>
                        ))}

                        {/* Projekte des Teams werden hier bewusst NICHT gelistet (störte nur,
                            09/2026) - Navigation zu Projekten läuft über die Projekte-Übersicht. */}
                      </>
                    )}
                  </div>
                );
              } })),

                // Team-übergreifende Punkte (z. B. Consulting)
                ...myTeamItems.map(item => ({ key: item.href, name: item.name, render: () => renderNavItem(item.name, item.href, item.icon) })),

                // MarketingManager-Button auch ohne aktives Marketing-Team, wenn Add-on/Modul aktiv ist
                ...(marketingAddonActive && !teams.some(t => t.team_type === 'team_marketing' && t.status === 'active') ? [{ key: 'marketing-manager', name: t('nav.marketingManager'), render: () => (
                <ContextMenu>
                  <ContextMenuTrigger asChild>
                    <button
                      onClick={() => { navigateToMarketing(); }}
                      className={`w-full flex items-center px-3 py-2 text-[12px] font-semibold rounded-lg transition-colors border-l-2 ${
                        (location.pathname.startsWith('/marketing') || (!!marketingProjektId && location.pathname === `/projects/${marketingProjektId}`))
                          ? 'bg-white/10 text-white border-l-[#E67E22]'
                          : 'text-white/60 hover:bg-white/5 hover:text-white/90 border-l-transparent'
                      }`}
                      data-testid="team-group-marketing-manager"
                    >
                      <div className="w-5 h-5 rounded flex items-center justify-center mr-3 shrink-0" style={{ backgroundColor: '#8B5CF6' }}>
                        <Megaphone className="w-3 h-3 text-white" />
                      </div>
                      <span className="flex-1 text-left truncate">{t('nav.marketingManager')}</span>
                      <ChevronRight className="w-3.5 h-3.5 text-white/40 shrink-0" />
                    </button>
                  </ContextMenuTrigger>
                  <ContextMenuContent className="w-56">
                    <ContextMenuItem
                      onClick={() => navigate('/marketing?tab=kampagnen&ansicht=kampagnen&kampagne=neu&phase=planung')}
                      data-testid="ctx-marketing-new-campaign"
                    >
                      Neue Kampagne erstellen
                    </ContextMenuItem>
                  </ContextMenuContent>
                </ContextMenu>
                ) }] : []),
              ], (e) => e.name, sprache).map((e) => <Fragment key={e.key}>{e.render()}</Fragment>)}
            </>
          )}
          </>
          )}

          {/* Gemeinsame Tools */}
          <div className="pt-3 pb-1 px-3">
            <span className="text-[9px] font-bold uppercase tracking-[0.12em] text-white/30">{t('nav.sectionTools')}</span>
          </div>
          {(() => {
            // Werkzeuge alphabetisch (Projektregel): normale Einträge + Orderboard + AI Workspace
            const perms = user?.permissions || [];
            const isAdmin = user?.role === 'admin' || user?.role === 'super_admin';
            const zeigeOrderboard = orderboardActive && !isGastro && (isAdmin || perms.includes('orderboard.use'));
            const eintraege = [
              ...globalItems.map((item) => ({
                key: item.href,
                name: item.name,
                render: () => renderNavItem(
                  item.name,
                  item.href,
                  item.icon,
                  item.href === '/emails' ? unreadCount
                    : item.badgeKey === 'chat' ? chatUnreadTotal
                    : 0
                ),
              })),
              // Orderboard im "Werkzeuge"-Bereich nur für NICHT-Gastro-Tenants
              // (bei Gastro erscheint es oben unter „Gastronomie", siehe gastroGlobalItems).
              ...(zeigeOrderboard ? [{ key: '/orderboard', name: 'Orderboard', render: () => renderNavItem('Orderboard', '/orderboard', 'ClipboardList') }] : []),
              { key: '/ai-workspace', name: t('nav.aiWorkspace'), render: () => (
                <button
                  onClick={() => { handleNavClick('/ai-workspace', 'AI Workspace', 'Sparkles'); }}
                  data-testid="nav-ai-workspace"
                  className={`
                    w-full flex items-center px-3 py-2 text-[12px] font-medium rounded-lg
                    transition-colors duration-150 text-left border-l-2
                    ${location.pathname.startsWith('/ai-workspace') || location.pathname.startsWith('/ai-assistant')
                      ? 'bg-white/10 text-white border-l-[#E67E22]'
                      : 'text-white/60 hover:bg-white/5 hover:text-white/90 border-l-transparent'}
                  `}
                >
                  <Sparkles className="w-5 h-5 mr-3" />
                  {t('nav.aiWorkspace')}
                  {aiConnected !== null && (
                    <span
                      className="ml-auto w-2 h-2 rounded-full flex-shrink-0"
                      style={{ backgroundColor: aiConnected ? '#E67E22' : 'rgba(255,255,255,0.3)' }}
                      title={aiConnected ? 'Claude AI verbunden' : 'Claude AI getrennt'}
                      data-testid="sidebar-ai-status-dot"
                    />
                  )}
                </button>
              ) },
            ];
            return nachAlphabet(eintraege, (e) => e.name, sprache).map((e) => <div key={e.key}>{e.render()}</div>);
          })()}
          {/* EasyWork Legal:
              Sidebar-Block bewusst entfernt (Architektur-Konvention 20.07.2026):
              Branchenmodule laufen über den Projekttyp-Mechanismus.
              - Mandatsakten & Kanzlei-Dashboard → Projekttyp „Mandatsakte"
              - RVG-Rechner + Legal-KI → Werkzeug-Kacheln im MandatsakteDashboard
              - Kollisionsprüfung → Inline im Projekt-Anlage-Dialog (Mandatsakte)
              - Fristen-Cockpit → Kachel in der Projekte-Übersicht (nur Legal-Tenants)
              Die Routen /legal/* bleiben für Deep-Links erreichbar.
          */}

          {/* Team hinzufuegen - nur Business-Tenants */}
          {!isStudent && !isFamily && (
          <div className="pt-2">
            <button
              onClick={() => { navigate('/team-catalog'); if (onNavigate) onNavigate(); }}
              data-testid="nav-team-catalog"
              className={`
                w-full flex items-center px-3 py-2 text-[12px] font-medium rounded-lg
                transition-colors duration-150 text-left border border-dashed
                ${location.pathname === '/team-catalog'
                  ? 'bg-white/10 text-white border-transparent'
                  : 'text-white/45 hover:bg-white/5 hover:text-white/90 border-white/15 hover:border-white/30'}
              `}
            >
              <Plus className="w-5 h-5 mr-3" />
              {t('nav.teamCatalog')}
            </button>
          </div>
          )}
        </div>
      </nav>

      {/* Footer — Einstellungen nur für Admin */}
      {user?.role === 'admin' && (
      <div className="border-t border-white/10 p-3">
        <button
          onClick={() => {
            openTab('/settings', 'Einstellungen', 'Settings', 'settings');
            navigate('/settings');
            if (onNavigate) onNavigate();
          }}
          className="w-full flex items-center px-3 py-2 text-[12px] font-medium rounded-lg text-white/60 hover:bg-white/5 hover:text-white/90 transition-colors text-left"
          data-testid="sidebar-settings-btn"
        >
          <Settings className="w-5 h-5 mr-3" />
          {t('nav.settings')}
        </button>
      </div>
      )}

      {/* Drag-Handle (nur Desktop, resizable) */}
      {resizable && (
        <div
          onMouseDown={(e) => startResize(e, 'right')}
          className="absolute top-0 right-0 h-full w-1 cursor-col-resize hover:bg-[#E67E22]/60 transition-colors z-10"
          title="Breite ziehen"
          data-testid="sidebar-resize-handle"
        />
      )}
    </div>
  );
}
